import React, { useState } from "react";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import { ClipboardCheck, ClipboardPlus, EnvelopeFill } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { CongSettings } from "../../../../types/scheduling/settings";
import { WMSchedule } from "../../../../types/scheduling/weekend";
import { ISODateString } from "../../../../types/date";
import { stringToLongLocaleDate } from "../../../../helpers/dateHelpers";
import { localizedTime } from "../../../../helpers/dateHelpers";
import HourglassGlobals from "../../../../helpers/globals";
import { renderWMTemplate } from "../../../../helpers/wmTemplates";
import { nameOfUser } from "../../../../helpers/user";
import { selectedCong } from "../../../../helpers/langGroups";

interface TemplateActionButtonProps {
  settings: CongSettings;
  schedule: WMSchedule;
  meetingDate: ISODateString;
  onEditClick?: () => void;
  editMode?: string;
  langGroupId: number;
}

export const TemplateActionButton: React.FC<TemplateActionButtonProps> = (props) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const schedule = props.schedule;
  const speakerEmail = schedule.speaker.email;
  const speaker = nameOfUser(schedule.speaker);
  const subject = t("email.templates.wm.public-talk-speaker-reminder.subject", {
    speaker,
    congregation: HourglassGlobals.cong?.name ?? "",
    date: stringToLongLocaleDate(props.meetingDate),
  });
  const workingCong = selectedCong(props.langGroupId) || HourglassGlobals.cong!;

  const renderTemplate = (): string =>
    renderWMTemplate(props.settings.wm_reminder_email_template, {
      speaker,
      talkTitle: schedule.public_talk?.number
        ? `${schedule.public_talk?.title} (${schedule.public_talk?.number})`
        : t("schedules.weekend.tbd"),
      date: stringToLongLocaleDate(props.meetingDate),
      time: localizedTime(workingCong.wmtime, i18n.language),
      congregation: workingCong.name ?? "",
      sender: HourglassGlobals.authUser.displayName,
    });

  const copyTemplateToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(renderTemplate());
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err: any) {
      console.error("Unable to copy to clipboard", err);
    }
  };

  return (
    <div className="d-flex flex-column">
      <Dropdown size="sm" as={ButtonGroup}>
        <Button
          variant="secondary"
          title={t("schedules.weekend.speaker.copy-reminder")}
          size="sm"
          className="py-2 ms-1 d-flex align-items-center"
          onClick={copyTemplateToClipboard}
        >
          {copied ? <ClipboardCheck color="green" size={18} /> : <ClipboardPlus size={18} />}
          <span className="ms-1">{t("general.copy")}</span>
        </Button>
        {speakerEmail && (
          <>
            <Dropdown.Toggle split variant="secondary" size="sm" />
            <Dropdown.Menu className="dropdown-menu-sm">
              <Dropdown.Item
                className="d-flex align-items-center dropdown-item-sm"
                href={`mailto:${speakerEmail}?subject=${subject}&body=${encodeURI(renderTemplate())}`}
                title={t("schedules.weekend.speaker.send-email-reminder")}
              >
                <EnvelopeFill className="me-1" size={18} />
                <span>{t("userinfo.email")}</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </>
        )}
      </Dropdown>
    </div>
  );
};
